import type { SelectorPackPlatform } from '@midiautolistening/selector-pack';
import { z } from 'zod';

/**
 * The social-platform catalog — the single place a platform is *described*.
 *
 * WHY THIS FILE EXISTS. "Mở rộng thêm kênh khác sau này phải dễ" is an
 * architectural requirement, not a compliment — the same one that produced
 * `ai-catalog.ts`. Concretely it means: adding TikTok, X or Zalo later must be
 * *registering a descriptor* (plus an agent-side automation module and a Prisma
 * enum member), not editing a `switch` in the gateway, a hardcoded "Facebook" in
 * forty dashboard strings and a URL regex in the command allowlist. Every layer
 * that needs a platform's name, hosts or entry URLs reads them from here.
 *
 * WHAT IS *NOT* HERE: anything that can drive a browser. Search-URL grammar,
 * selector baselines and DOM parsing live in
 * `apps/agent/src/automation/platforms/<id>/` — versioned agent code. A test on
 * the agent side asserts that every catalog id has an automation module, so a
 * platform that is described but not implemented fails the build instead of
 * shipping a project form that offers a channel leading nowhere.
 *
 * Adding a platform therefore touches exactly four places, and no others:
 *   1. this tuple + its catalog entry below,
 *   2. `packages/selector-pack` — one lowercase id in `SELECTOR_PACK_PLATFORMS`,
 *   3. `apps/agent/src/automation/platforms/<id>/` (baseline packs + URL grammar),
 *   4. the Prisma `Platform` enum + a migration.
 * There is no fifth: the dashboard renders channel pickers, badges and copy from
 * this catalog, and the API resolves hosts and labels from it.
 */

// ---------------------------------------------------------------------------
// Platform ids
// ---------------------------------------------------------------------------

/**
 * Every platform the product knows about, as a tuple so `z.enum` can consume it.
 *
 * A closed enum for the same reason `AI_PROVIDER_IDS` is one: `projects.platform`
 * and `posts.platform` are PostgreSQL enum columns, so a migration is unavoidable
 * for a new member anyway — and the enum buys exhaustiveness checks and compile
 * errors where a free string would buy a 500 at runtime.
 *
 * `enums.ts` re-exports this tuple as `PLATFORMS`; the Prisma `Platform` enum
 * mirrors it character-for-character.
 */
export const PLATFORM_IDS = ['FACEBOOK', 'THREADS'] as const;
export type PlatformId = (typeof PLATFORM_IDS)[number];

// ---------------------------------------------------------------------------
// Descriptor
// ---------------------------------------------------------------------------

export interface PlatformDescriptor {
  readonly id: PlatformId;
  /** Full display name — "Facebook", "Threads". Used wherever the UI names the channel. */
  readonly label: string;
  /** Short label for tight columns and buttons — "FB", "Threads". */
  readonly shortLabel: string;
  /**
   * The lowercase id used inside SelectorPack documents
   * (`SELECTOR_PACK_PLATFORMS` in `packages/selector-pack`). Two vocabularies
   * exist for historical reasons; this mapping is the single bridge between
   * them, so no service ever hardcodes `'facebook'` against a `'FACEBOOK'` row.
   */
  readonly packPlatform: SelectorPackPlatform;
  /**
   * Hosts the agent may navigate to for this platform. Matched as the exact
   * host or any subdomain of it. This list feeds the navigation allowlist in
   * the Playwright adapter — the second gate behind `platformUrlSchema`.
   */
  readonly allowedHosts: readonly string[];
  /** Where a session probe lands when the current URL is not on the platform. */
  readonly homeUrl: string;
  /** Where `browser.open_login` sends the HEADED window for a manual sign-in. */
  readonly loginUrl: string;
  /**
   * Source of the RegExp a navigable URL of this platform must match. Kept as a
   * string so the catalog stays pure data; compile with `platformUrlRegex()`.
   */
  readonly urlPatternSource: string;
  /** Whether the platform has a "group" concept worth deep-searching into. */
  readonly supportsGroupSearch: boolean;
  /** One Vietnamese sentence for the channel picker in the project form. */
  readonly description: string;
}

/**
 * Display order here is picker order in the dashboard.
 */
export const PLATFORM_CATALOG: Readonly<Record<PlatformId, PlatformDescriptor>> = {
  FACEBOOK: {
    id: 'FACEBOOK',
    label: 'Facebook',
    shortLabel: 'FB',
    packPlatform: 'facebook',
    allowedHosts: ['facebook.com'],
    homeUrl: 'https://www.facebook.com/',
    loginUrl: 'https://www.facebook.com/login',
    urlPatternSource: '^https:\\/\\/(?:[a-z0-9-]+\\.)?facebook\\.com\\/',
    supportsGroupSearch: true,
    description: 'Tìm bài viết công khai trên Facebook: trang cá nhân, fanpage và nhóm.',
  },
  THREADS: {
    id: 'THREADS',
    label: 'Threads',
    shortLabel: 'Threads',
    packPlatform: 'threads',
    /** `threads.net` was the launch domain and still redirects; both stay allowed
     *  so a stored permalink from either era remains navigable. */
    allowedHosts: ['threads.com', 'threads.net'],
    homeUrl: 'https://www.threads.com/',
    loginUrl: 'https://www.threads.com/login',
    urlPatternSource: '^https:\\/\\/(?:[a-z0-9-]+\\.)?threads\\.(?:com|net)\\/',
    supportsGroupSearch: false,
    description: 'Tìm thread công khai trên Threads (tài khoản đăng nhập bằng Instagram).',
  },
} as const;

/** Descriptors in picker order. */
export const PLATFORM_LIST: readonly PlatformDescriptor[] = PLATFORM_IDS.map(
  (id) => PLATFORM_CATALOG[id],
);

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

export function getPlatformDescriptor(id: PlatformId): PlatformDescriptor {
  return PLATFORM_CATALOG[id];
}

export function platformLabel(id: PlatformId): string {
  return PLATFORM_CATALOG[id].label;
}

export function platformShortLabel(id: PlatformId): string {
  return PLATFORM_CATALOG[id].shortLabel;
}

/**
 * Uppercase wire/DB id → lowercase SelectorPack id.
 */
export function packPlatformOf(id: PlatformId): SelectorPackPlatform {
  return PLATFORM_CATALOG[id].packPlatform;
}

/**
 * Lowercase SelectorPack id → uppercase wire/DB id.
 *
 * Total by construction: `SELECTOR_PACK_PLATFORMS` and this catalog list the
 * same platforms, and the contracts test suite asserts the bijection — so the
 * lookup cannot miss unless the two files drifted, which is exactly what the
 * test exists to catch.
 */
export function platformOfPackPlatform(packPlatform: SelectorPackPlatform): PlatformId {
  const found = PLATFORM_LIST.find((entry) => entry.packPlatform === packPlatform);
  if (!found) {
    throw new Error(`SelectorPack platform không có trong catalog: ${packPlatform}`);
  }
  return found.id;
}

// ---------------------------------------------------------------------------
// URL discipline
// ---------------------------------------------------------------------------

/** Compiled (case-insensitive) URL pattern for one platform. */
export function platformUrlRegex(id: PlatformId): RegExp {
  return new RegExp(PLATFORM_CATALOG[id].urlPatternSource, 'i');
}

export function isPlatformUrl(id: PlatformId, url: string): boolean {
  return platformUrlRegex(id).test(url);
}

/** Which catalog platform a URL belongs to, or null when none matches. */
export function platformOfUrl(url: string): PlatformId | null {
  for (const entry of PLATFORM_LIST) {
    if (platformUrlRegex(entry.id).test(url)) return entry.id;
  }
  return null;
}

/**
 * True when `host` is one of the platform's allowed hosts or a subdomain of one.
 * Pure string logic so both the API and the agent's Playwright adapter can share
 * it — the two gates must not drift.
 */
export function isHostAllowedForPlatform(id: PlatformId, host: string): boolean {
  const lowered = host.toLowerCase();
  return PLATFORM_CATALOG[id].allowedHosts.some(
    (allowed) => lowered === allowed || lowered.endsWith(`.${allowed}`),
  );
}

/**
 * A URL the agent is allowed to navigate to, on ANY known platform.
 *
 * This replaces the old `facebookUrlSchema` on the command channel. It is still
 * a closed allowlist — "navigate this machine anywhere" remains inexpressible —
 * just closed over the catalog instead of one hardcoded host. Platform-specific
 * binding happens at the two ends: the server only ever fills these fields from
 * its own stored rows (whose platform it knows), and the agent re-checks the
 * host against the *profile's* platform before navigating.
 */
export const platformNavigableUrlSchema = z
  .string()
  .url()
  .max(2_048)
  .refine((value) => platformOfUrl(value) !== null, {
    message: 'URL phải thuộc một nền tảng được hỗ trợ (Facebook, Threads)',
  });
